import { useState, useEffect } from 'react'
import { Box, Typography, useTheme, useMediaQuery, CircularProgress } from '@mui/material';
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { io } from 'socket.io-client';

// Fiyatlar backend'in Harem Altın soketinden aktardığı price_changed olayıyla gelir;
// her olay yalnızca değişen sembolleri taşır, bu yüzden gelenler birleştirilir.
const SOCKET_URL = 'https://refinera.com.tr';

interface SymbolPrice {
  code: string;
  alis: string | number;
  satis: string | number;
  tarih: string;
  dir: {
    alis_dir: string;
    satis_dir: string;
  };
  dusuk: string | number;
  yuksek: string | number;
  kapanis: string | number;
}

interface PriceChangedPayload {
  meta?: { time: number };
  data?: { [key: string]: SymbolPrice };
}

const SYMBOLS: { code: string; title: string }[] = [
  { code: 'AYAR22', title: 'Ayar 22' },
  { code: 'KULCEALTIN', title: 'Kulçe Altın' },
  { code: 'CEYREK_YENI', title: 'Çeyrek Yeni' },
  { code: 'CEYREK_ESKI', title: 'Çeyrek Eski' },
  { code: 'YARIM_YENI', title: 'Yarım Yeni' },
  { code: 'YARIM_ESKI', title: 'Yarım Eski' },
  { code: 'TEK_YENI', title: 'Tek Yeni' },
  { code: 'TEK_ESKI', title: 'Tek Eski' },
  { code: 'ATA_YENI', title: 'Ata Yeni' },
  { code: 'ATA_ESKI', title: 'Ata Eski' },
  { code: 'ATA5_YENI', title: 'Ata 5 Yeni' },
  { code: 'ATA5_ESKI', title: 'Ata 5 Eski' },
  { code: 'GREMESE_YENI', title: 'Gremese Yeni' },
  { code: 'GREMESE_ESKI', title: 'Gremese Eski' },
  { code: 'AYAR14', title: 'Ayar 14' },
];

const toNumber = (v: string | number | undefined) => parseFloat(String(v ?? '0')) || 0;

function toCurrencyData(title: string, p: SymbolPrice): CurrencyData {
  const satis = toNumber(p.satis);
  const kapanis = toNumber(p.kapanis);
  return {
    title,
    value: satis.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
    up: p.dir?.satis_dir === 'up',
    percent: `%${((satis - kapanis) / (kapanis || 1) * 100).toFixed(2)}`,
    amount: (satis - kapanis).toFixed(2),
  };
}

interface CurrencyData {
  title: string;
  value: string;
  up: boolean;
  percent: string;
  amount: string;
}

function PriceCard({ title, value, up, percent, amount }: CurrencyData) {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex !important',
        flexDirection: 'column',
        alignItems: 'center',
        minWidth: { xs: 100, sm: 120 },
        height: '100%',
        px: 2,
        py: 1,
        borderRight: '1px solid #223C3B',
        '&:last-child': { borderRight: 'none' },
      }}
    >
      <Typography variant="body2" color="#E6E6E6" sx={{ fontWeight: 400 }}>{title}</Typography>
      <Typography variant="h6" color="#fff" sx={{ fontWeight: 700 }}>{value}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
        <Box
          sx={{
            width: 16,
            height: 16,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: up ? theme.palette.success.main : theme.palette.error.main,
          }}
        >
          {up ? '▲' : '▼'}
        </Box>
        <Box
          sx={{
            bgcolor: up ? 'rgba(28,186,118,0.1)' : 'rgba(212,61,40,0.1)',
            color: up ? theme.palette.success.main : theme.palette.error.main,
            borderRadius: 1,
            px: 1,
            fontSize: 13,
            fontWeight: 500,
            mr: 0.5,
          }}
        >
          {percent}
        </Box>
        <Box
          sx={{
            bgcolor: up ? 'rgba(28,186,118,0.1)' : 'rgba(212,61,40,0.1)',
            color: up ? theme.palette.success.main : theme.palette.error.main,
            borderRadius: 1,
            px: 1,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          {amount}
        </Box>
      </Box>
    </Box>
  );
}

export default function CurrencySlider() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [prices, setPrices] = useState<{ [code: string]: SymbolPrice }>({});

  useEffect(() => {
    const socket = io(SOCKET_URL, { transports: ['websocket'] });

    socket.on('price_changed', (payload: PriceChangedPayload) => {
      if (!payload?.data) return;
      setPrices(prev => ({ ...prev, ...payload.data }));
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const currencyData = SYMBOLS
    .filter(s => prices[s.code])
    .map(s => toCurrencyData(s.title, prices[s.code]));
  const loading = currencyData.length === 0;

  // Ekran boyutuna göre gösterilecek kart sayısını belirleme
  let slidesToShow = 8; // Varsayılan tam ekran - daha fazla altın türü için artırıldı
  if (isTablet) slidesToShow = 6; // Tablet boyutunda 6 kart
  if (isMobile) slidesToShow = 3; // Mobil boyutunda 3 kart
  
  const sliderSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: slidesToShow,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    cssEase: "linear",
    arrows: false,
    responsive: [
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: 6,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 900,
        settings: {
          slidesToShow: 4,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 600,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      }
    ]
  };
  
  return (
    <Box sx={{
      width: '100%',
      bgcolor: '#011B1C',
      px: { xs: 0.5, md: 2 },
    }}>
      <Box sx={{ 
        maxWidth: '1440px', 
        mx: 'auto',
        '.slick-track': { 
          display: 'flex', 
          alignItems: 'center',
          height: '100%'
        },
        '.slick-slide': {
          height: 'auto',
          '& > div': { height: '100%' }
        }
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          </Box>
        ) : (
          <Slider {...sliderSettings}>
            {currencyData.map((item) => (
              <PriceCard key={item.title} {...item} />
            ))}
          </Slider>
        )}
      </Box>
    </Box>
  );
}
